import * as THREE from 'three';

/**
 * The street surface, drawn in the shader: tarmac that has been dug up and patched, pavement
 * laid in slabs, granite kerbs — and all three wet when it is raining at the real place.
 * DECISIONS.md §83.
 *
 * The road was one flat colour per class of road, which is the single largest area of the
 * frame from a chase camera and read as exactly what it was: grey paint. A street is the
 * record of everything that has been done to it. Slop New York draws that procedurally —
 * utility cuts in fresh black, tar sealing its cracks, manholes, oil under where cars stand
 * — and nothing of it needs to know where the lanes are, so it is drawn in world space from
 * hashes of a few grids, the same everywhere for everyone.
 *
 * **Wet.** Wet asphalt is darker and smooth, so it reflects; standing water reflects like a
 * mirror. Both come from one uniform, `roadWet`, which the weather drives (§31), so a race
 * in Kraków in the rain is on a wet road without anybody choosing that. What a puddle
 * reflects is the environment map — the sky — plus, after dark, the street lamps, which
 * `streetLight.ts` adds as streaks along the reflected ray (§84).
 *
 * The same rule as the facades for anything fine: filtered over the pixel, and faded to its
 * average once it is smaller than one, so the far end of a long avenue does not crawl.
 */

export const SURFACE_TARMAC = 0;
export const SURFACE_PAVEMENT = 1;
export const SURFACE_KERB = 2;
/**
 * A footway, a park path, a pedestrian street: laid in setts. They were drawn as tarmac, with
 * the carriageway's manholes, oil and utility cuts, so a path across a park had a drain cover
 * every fourteen metres and a car's oil stain under a bench (§93).
 */
export const SURFACE_PATH = 3;

const GLSL_PARS = /* glsl */ `
uniform float roadWet;
varying vec3 vRoadWorld;
varying float vRoadSurface;
varying vec2 vRoadAxis;

struct RoadOut { vec3 albedo; float rough; float metal; };

float rHash( vec2 p ) {
	vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
	p3 += dot( p3, p3.yzx + 33.33 );
	return fract( ( p3.x + p3.y ) * p3.z );
}
float rNoise( vec2 p ) {
	vec2 i = floor( p );
	vec2 f = fract( p );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( rHash( i ), rHash( i + vec2( 1.0, 0.0 ) ), f.x ),
		mix( rHash( i + vec2( 0.0, 1.0 ) ), rHash( i + vec2( 1.0, 1.0 ) ), f.x ), f.y );
}
float rBand( float p, float lo, float hi, float w ) {
	return clamp( ( min( p + w, hi ) - max( p - w, lo ) ) / ( 2.0 * w ), 0.0, 1.0 );
}
float rPulseI( float t, float a, float b ) { return floor( t ) * ( b - a ) + clamp( fract( t ), a, b ) - a; }
float rPulse( float x, float a, float b, float w ) {
	return ( rPulseI( x + w, a, b ) - rPulseI( x - w, a, b ) ) / ( 2.0 * w );
}
// A thin line where a noise field crosses one level: cracks and their tar.
float rLine( vec2 p, float scale, float width, float pw ) {
	float n = rNoise( p * scale ) + 0.5 * rNoise( p * scale * 2.3 + 17.0 );
	float w = max( fwidth( n ), 1e-4 );
	return 1.0 - smoothstep( width, width + w * 1.5, abs( n - 0.75 ) );
}

RoadOut road() {
	RoadOut o;
	vec2 p = vRoadWorld.xz;
	// Metres covered by one pixel here, and how far below that the fine detail fades.
	float px = max( length( fwidth( p ) ), 1e-4 );
	float fine = 1.0 - smoothstep( 0.04, 0.16, px );
	float mid = 1.0 - smoothstep( 0.2, 0.8, px );

	vec3 base = vColor.rgb;
	float rough = 0.88;
	float metal = 0.0;

	if ( vRoadSurface < 0.5 ) {
		// --- tarmac ---------------------------------------------------------------------
		// Neutral, not the blue-grey the class palette leans: asphalt is black stone in bitumen,
		// bleached grey by the sun, and darker where it has been relaid.
		float l = dot( base, vec3( 0.2126, 0.7152, 0.0722 ) );
		vec3 col = mix( vec3( l ), base, 0.35 ) * vec3( 1.02, 1.0, 0.97 ) * 0.92;
		col *= 0.9 + 0.2 * rNoise( p * 0.05 ) ;
		col *= 1.0 + ( rNoise( p * 1.7 ) - 0.5 ) * 0.12 * mid;
		col *= 1.0 + ( rHash( floor( p * 22.0 ) ) - 0.5 ) * 0.18 * fine;

		// Utility cuts: a rectangle of newer, blacker tarmac in some 9 m cells, with a sealed edge.
		// (Not \`patch\`, which GLSL ES 3.0 reserves.)
		vec2 cell = floor( p / 9.0 );
		vec2 local = p - cell * 9.0;
		float h = rHash( cell + 3.1 );
		if ( h < 0.33 ) {
			vec2 lo = vec2( rHash( cell + 1.7 ), rHash( cell + 5.3 ) ) * 4.0 + 0.5;
			vec2 size = vec2( 1.4 + rHash( cell + 2.9 ) * 3.5, 0.9 + rHash( cell + 8.1 ) * 3.0 );
			vec2 pw = vec2( px * 0.5 );
			float cut = rBand( local.x, lo.x, lo.x + size.x, pw.x ) * rBand( local.y, lo.y, lo.y + size.y, pw.y );
			float rim = cut * ( 1.0 - rBand( local.x, lo.x + 0.06, lo.x + size.x - 0.06, pw.x ) * rBand( local.y, lo.y + 0.06, lo.y + size.y - 0.06, pw.y ) );
			col = mix( col, col * 0.72, cut );
			col = mix( col, vec3( 0.03 ), rim * mid * 0.8 );
		}
		// Tar snakes: the black lines a crew pours into cracks.
		// Sparse: a few runs in a street, not a network over all of it.
		float tar = rLine( p, 0.35, 0.006, px ) * smoothstep( 0.64, 0.8, rNoise( p * 0.06 + 9.0 ) );
		col = mix( col, vec3( 0.03 ), tar * mid * 0.6 );
		// Fine cracks, near the camera only.
		float crack = rLine( p, 1.6, 0.004, px ) * smoothstep( 0.68, 0.86, rNoise( p * 0.1 - 4.0 ) );
		col *= 1.0 - 0.3 * crack * fine;
		// Oil where cars stand: one blot in four 3 m cells, darker and a little glossy.
		vec2 oc = floor( p / 3.0 );
		float oh = rHash( oc + 11.0 );
		vec2 oPos = ( oc + vec2( 0.3 + 0.4 * rHash( oc + 2.0 ), 0.3 + 0.4 * rHash( oc + 4.0 ) ) ) * 3.0;
		float oil = ( 1.0 - smoothstep( 0.15, 0.55 + 0.3 * oh, length( ( p - oPos ) * vec2( 1.0, 1.6 ) ) ) ) * step( 0.75, oh );
		col *= 1.0 - 0.3 * oil;
		rough -= 0.2 * oil;
		// Manholes: one in three 14 m cells, an iron disc with a raised rim.
		vec2 mc = floor( p / 14.0 );
		vec2 mPos = ( mc + vec2( 0.25 + 0.5 * rHash( mc + 21.0 ), 0.25 + 0.5 * rHash( mc + 23.0 ) ) ) * 14.0;
		float md = length( p - mPos );
		float lid = ( 1.0 - smoothstep( 0.32, 0.32 + px, md ) ) * step( rHash( mc + 25.0 ), 0.34 );
		float ring = lid * smoothstep( 0.26, 0.28, md );
		float grid = rPulse( ( p.x + p.y ) * 9.0, 0.0, 0.35, max( px * 4.5, 1e-3 ) );
		col = mix( col, vec3( 0.07, 0.068, 0.065 ) * ( 0.8 + 0.4 * grid * fine ) + 0.04 * ring, lid );
		rough = mix( rough, 0.55, lid );
		metal = mix( metal, 0.5, lid );
		base = col;
	} else if ( vRoadSurface < 1.5 ) {
		// --- pavement: slabs laid along the street ------------------------------------------
		vec2 axis = normalize( vRoadAxis + vec2( 1e-5 ) );
		vec2 q = vec2( dot( p, axis ), dot( p, vec2( -axis.y, axis.x ) ) );
		vec2 slab = floor( q / vec2( 1.0, 0.75 ) );
		float qw = max( px, 1e-3 );
		float joint = max( rPulse( q.x, -0.012, 0.012, qw * 0.5 ), rPulse( q.y / 0.75, -0.016, 0.016, qw * 0.5 / 0.75 ) );
		float tint = rHash( slab + 7.0 );
		vec3 col = base * ( 0.92 + 0.14 * tint );
		// One slab in seven is a replacement, newer and paler.
		col = mix( col, base * 1.12, step( 0.86, rHash( slab + 13.0 ) ) );
		col *= 1.0 - 0.3 * joint * mid;
		// Chewing gum, which is what a city pavement is actually covered in.
		vec2 gc = floor( q * 2.0 );
		vec2 gPos = ( gc + vec2( rHash( gc ), rHash( gc + 3.0 ) ) ) / 2.0;
		float gum = ( 1.0 - smoothstep( 0.02, 0.035, length( q - gPos ) ) ) * step( 0.93, rHash( gc + 5.0 ) );
		col = mix( col, col * 0.7, gum * fine );
		base = col;
		rough = 0.9;
	} else if ( vRoadSurface > 2.5 ) {
		// --- path: setts in an irregular bond -----------------------------------------------
		// Cells of a jittered grid, nearest centre wins: stones with no direction, because a
		// path ribbon carries none and winds where it likes. The joint is where the nearest two
		// centres are about equally near.
		vec2 g = p / 0.42;
		vec2 gi = floor( g );
		float d1 = 8.0;
		float d2 = 8.0;
		vec2 stone = gi;
		for ( int j = -1; j <= 1; j ++ ) {
			for ( int i = -1; i <= 1; i ++ ) {
				vec2 c = gi + vec2( float( i ), float( j ) );
				vec2 at = c + 0.2 + 0.6 * vec2( rHash( c + 31.0 ), rHash( c + 47.0 ) );
				float d = length( g - at );
				if ( d < d1 ) { d2 = d1; d1 = d; stone = c; }
				else if ( d < d2 ) d2 = d;
			}
		}
		float edge = d2 - d1;
		float jw = max( fwidth( edge ), 1e-3 );
		float joint = 1.0 - smoothstep( 0.04, 0.04 + jw * 1.5, edge );
		vec3 col = base * ( 0.9 + 0.18 * rHash( stone + 3.0 ) );
		col *= 0.92 + 0.16 * rNoise( p * 0.08 );
		col *= 1.0 - 0.32 * joint * mid;
		base = col;
		rough = 0.92;
	} else {
		// --- kerb stone: granite, in metre-and-a-half lengths ----------------------------
		vec2 axis = normalize( vRoadAxis + vec2( 1e-5 ) );
		float along = dot( p, axis );
		float joint = rPulse( along / 1.5, -0.01, 0.01, max( px / 1.5, 1e-3 ) * 0.5 );
		base *= ( 0.94 + 0.12 * rHash( vec2( floor( along / 1.5 ), 0.0 ) ) ) * ( 1.0 - 0.3 * joint * mid );
		base *= 0.92 + 0.16 * rHash( floor( p * 30.0 ) ) * fine;
		rough = 0.75;
	}

	// --- wet ----------------------------------------------------------------------------
	// Damp darkens and smooths everything; standing water collects in the low spots, which a
	// noise field stands in for, and spreads as the rain gets heavier.
	if ( roadWet > 0.0 ) {
		float damp = roadWet;
		float field = rNoise( p * 0.18 ) * 0.7 + rNoise( p * 0.7 + 3.0 ) * 0.3;
		float threshold = mix( 0.92, 0.52, roadWet );
		float puddle = smoothstep( threshold, threshold + 0.04, field ) * step( vRoadSurface, 1.5 );
		// Drains better than the carriageway, and is laid to a fall: the pavement holds less.
		puddle *= mix( 1.0, 0.35, step( 0.5, vRoadSurface ) );
		base *= mix( 1.0, 0.62, damp );
		rough = mix( rough, 0.3, damp );
		base *= mix( 1.0, 0.55, puddle );
		rough = mix( rough, 0.03, puddle );
	}

	o.albedo = base;
	o.rough = clamp( rough, 0.02, 1.0 );
	o.metal = metal;
	return o;
}
`;

export interface RoadShader {
  /** 0 dry, 1 standing water everywhere it collects. */
  setWet(amount: number): void;
}

/**
 * Turns the road mesh's `MeshStandardMaterial` into the street. Sets `onBeforeCompile`
 * outright, so it goes first; the sky field and the street lights chain after it.
 */
export function applyRoadShader(material: THREE.MeshStandardMaterial): RoadShader {
  const uniforms = { roadWet: { value: 0 } };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float surface;\nattribute vec2 streetAxis;\n' +
          'varying vec3 vRoadWorld;\nvarying float vRoadSurface;\nvarying vec2 vRoadAxis;',
      )
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n' +
          '\tvRoadWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;\n' +
          '\tvRoadSurface = surface;\n\tvRoadAxis = streetAxis;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${GLSL_PARS}\nvoid main() {`)
      .replace('#include <color_fragment>', '\tRoadOut roadOut = road();\n\tdiffuseColor.rgb = roadOut.albedo;')
      .replace('#include <roughnessmap_fragment>', '\tfloat roughnessFactor = roadOut.rough;')
      .replace('#include <metalnessmap_fragment>', '\tfloat metalnessFactor = roadOut.metal;');
  };
  material.customProgramCacheKey = () => 'road';
  return {
    setWet(amount) {
      uniforms.roadWet.value = Math.max(0, Math.min(1, amount));
    },
  };
}
